import React, { FC, useEffect } from 'react';
import { observer } from 'mobx-react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useToast from '@yaydoo/react-components/lib/hooks/useToast';
import useModal from '@yaydoo/react-components/lib/hooks/useModal';

import Redirect3dSecure from '@components/modals/Redirect3dSecure';
import { PartnerFormProps } from '@containers/checkout/payment-form/types';
import PayButton from '@components/PayButton';
import useStore from '@hooks/useStore';
import { FormPayload } from '@store/openPay';
import { FieldValues } from 'react-hook-form/dist/types';
import GenericCardFields from '@containers/checkout/payment-form/CardPaymentForm/GenericCardFields';
import { Partner } from '@constants/paymentLink';
import useStyles from './styles';
import FormPartnerLogo from '@components/FormPartnerLogo';
import Grid from '@material-ui/core/Grid';
import ErrorIcon from '@material-ui/icons/Error';

const OpenPayCardForm: FC<PartnerFormProps> = ({ onValidationError }) => {
  const css = useStyles();
  const { handleSubmit } = useFormContext();
  const { showModal } = useModal();
  const { openToast } = useToast();
  const { t } = useTranslation();
  const {
    openPay,
    links: {
      paymentLink: { paymentLinkId, total: amount, isSubscription },
    },
    checkout,
    sura,
  } = useStore();
  const { setErrorMessage, errorMessage } = openPay;
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const transactionId = urlParams.get('id');

  const onSubmit = async (params: FormPayload) => {
    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const { metadata, success } = await openPay.pay({
        ...params,
        paymentLinkId,
        isSura: sura.isSura,
      });
      const shouldSkip3DS = metadata?.redirectUrl === undefined && success;

      if (shouldSkip3DS) {
        checkout.setCardPaymentSuccess();
      } else {
        showModal({
          component: Redirect3dSecure,
          props: { redirectUrl: metadata.redirectUrl, amount },
        });
      }
    } catch (e: any) {
      setErrorMessage(e?.errors?.error_code || '');
    }
  };

  const confirm3ds = async (transactionId: string) => {
    try {
      const response = await openPay.confirm3dsPaymentStatus({
        partner: Partner.OPENPAY,
        transactionId,
      });
      if (response.status === 'COMPLETED') {
        checkout.setCardPaymentSuccess();
      } else {
        openToast({
          message: response.errorMessage,
          title: t('checkoutScreen.payment.errors.card.title'),
          type: 'error',
        });
      }
    } catch (e: any) {
      setErrorMessage(e.errors.error_code);
    }
  };

  useEffect(() => {
    if (transactionId) {
      if (location.href.includes('?')) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        history.pushState({}, null, location.href.split('?')[0]);
      }
      confirm3ds(transactionId);
    }
  }, [transactionId]);

  const CardPaymentError: React.FC = () => (
    <Grid item className={css.paymentError}>
      <ErrorIcon /> {errorMessage}
    </Grid>
  );

  return (
    <>
      {errorMessage !== '' && <CardPaymentError />}
      <GenericCardFields errorFields={errorMessage !== ''} />
      <PayButton
        onClick={handleSubmit(onSubmit as SubmitHandler<FieldValues>, onValidationError)}
        disabled={openPay.isProcessing || !checkout.isAllowedToPayWithCard}
        loading={openPay.isProcessing}
        isSubscription={isSubscription}
        label={
          !isSubscription
            ? t('checkoutScreen.payment.methods.submit')
            : t('checkoutScreen.payment.methods.subscribe')
        }
      />
      <FormPartnerLogo partner={Partner.OPENPAY} />
    </>
  );
};

export default observer(OpenPayCardForm);
